import axios from 'axios';
import { Address, GoogleAddressAutoComplete, AddressFormDetails } from 'models/contact';
import { CONSTANTS } from 'config/constants';
import * as htmlparser2 from 'htmlparser2';

const { GOOGLE_PLACES_API_KEY, GOOGLE_PLACES_API_BASE_URL } = CONSTANTS;

export const addressAutoCompleteSearch = async (
    input: string,
    countryCode: string,
    language: string,
): Promise<GoogleAddressAutoComplete[]> => {
    try {
        const response = await axios.post(
            `${GOOGLE_PLACES_API_BASE_URL}/v1/places:autocomplete`,
            {
                input,
                languageCode: language,
                includedPrimaryTypes: ['premise', 'street_address', 'route', 'street_number'],
                includedRegionCodes: [countryCode],
                regionCode: countryCode,
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                    'X-Goog-Api-Key': GOOGLE_PLACES_API_KEY,
                    'X-Goog-FieldMask':
                        'suggestions.placePrediction.placeId,suggestions.placePrediction.text.text,suggestions.placePrediction.types',
                },
            },
        );
        return convertAutocompleteResponseData(response.data);
    } catch (error) {
        console.error(`Error while fetching address autocomplete: ${error}`);
        return [];
    }
};

export const addressDetails = async (placeId: string): Promise<AddressFormDetails> => {
    try {
        const response = await axios.get(`${GOOGLE_PLACES_API_BASE_URL}/v1/places/${placeId}`, {
            headers: {
                'Content-Type': 'application/json',
                'X-Goog-Api-Key': GOOGLE_PLACES_API_KEY,
                'X-Goog-FieldMask': 'id,location,addressComponents,adrFormatAddress,types',
            },
        });

        const country = response.data.addressComponents.find((component: any) => component.types.includes('country'));
        return {
            googleId: response.data.id,
            address: convertMicroAdrToAddress(response.data, country.shortText || ''),
            isCompleteAddress:
                response.data.types.includes('street_address') || response.data.types.includes('premise'),
        };
    } catch (error) {
        console.error(`Error while fetching address details: ${error}`);
        throw error;
    }
};

export const geocodeAddress = async (address: Address, language: string) => {
    const autocompleteResult = await addressAutoCompleteSearch(
        `${address.street}, ${address.postalCode} ${address.city}`,
        address.country || 'SE',
        language,
    );
    if (autocompleteResult.length > 0) {
        const details = await addressDetails(autocompleteResult[0].googleId);
        if (details.address.geoLocation) {
            return { ...address, geoLocation: details.address.geoLocation };
        }
    }
    return address;
};

const convertAutocompleteResponseData = (data: any) => {
    if (data.suggestions) {
        return data.suggestions.map((suggestion: any) => {
            return {
                googleId: suggestion.placePrediction.placeId,
                text: suggestion.placePrediction.text.text,
                type: suggestion.placePrediction.types.filter((type: string) => type !== 'geocode')[0],
            };
        });
    }
    return [];
};

const convertMicroAdrToAddress = (data: any, countryCode: string) => {
    const dom = htmlparser2.parseDocument(data.adrFormatAddress);
    const address: Address = {};
    address.country = countryCode;
    address.geoLocation = {
        latitude: data.location.latitude,
        longitude: data.location.longitude,
    };
    dom.children.forEach((child: any) => {
        if (child.name === 'span') {
            switch (child.attribs.class) {
                case 'street-address':
                    address.street = child.children[0].data;
                    break;
                case 'extended-address':
                    address.street2 = child.children[0].data;
                    break;
                case 'postal-code':
                    address.postalCode = child.children[0].data;
                    break;
                case 'locality':
                    address.city = child.children[0].data;
                    break;
                case 'region':
                    address.stateProvince = child.children[0].data;
                    break;
                default:
                    break;
            }
        }
    });

    return address;
};
